import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Send, Loader2, Calendar } from "lucide-react";
import { API } from "@/api";


/**
 * Iter81 — Page publique de contact / prise de rendez-vous.
 *
 * Aucune authentification requise. POSTe sur `/api/website/form` (public).
 * Le cabinet voit le lead dans le widget Trafic site web du dashboard.
 */
export default function ContactPublic() {
  const [data, setData] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  // Iter81 — Track la visite (silencieux, on s'en fout du resultat)
  if (typeof window !== "undefined" && !window.__visitTracked) {
    window.__visitTracked = true;
    axios.post(`${API}/website/visit`, {
      page: "/contact-public",
      referrer: document.referrer || "",
      user_agent: navigator.userAgent,
    }).catch(() => {});
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!data.name.trim()) return setError("Nom requis");
    if (!data.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) return setError("Courriel invalide");
    if (!data.message.trim()) return setError("Message requis");
    setSending(true);
    try {
      await axios.post(`${API}/website/form`, {
        form_type: "rdv_request",
        name: data.name,
        email: data.email,
        message: data.phone ? `Tel: ${data.phone}\n\n${data.message}` : data.message,
      });
      setSent(true);
    } catch {
      setError("Echec de l'envoi. Reessayez ou ecrivez-nous directement.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center" data-testid="contact-success">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Demande envoyee !</h1>
          <p className="text-sm text-gray-600">
            Merci, {data.name}. Nous vous contacterons sous 1 jour ouvrable a l'adresse <b>{data.email}</b>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
      <form onSubmit={submit}
        className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 w-full max-w-lg space-y-4"
        data-testid="contact-public-form">
        <div className="text-center mb-4">
          <Calendar className="w-10 h-10 text-blue-600 mx-auto mb-2" />
          <h1 className="text-2xl font-bold text-gray-800">Prendre rendez-vous</h1>
          <p className="text-sm text-gray-500 mt-1">
            Repondez en moins d'une minute. Nous vous recontactons sous 24h.
          </p>
        </div>

        <Field label="Nom complet *">
          <input type="text" value={data.name}
            onChange={e => setData({ ...data, name: e.target.value })}
            className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required data-testid="contact-name" />
        </Field>

        <Field label="Courriel *">
          <input type="email" value={data.email}
            onChange={e => setData({ ...data, email: e.target.value })}
            className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required data-testid="contact-email" />
        </Field>

        <Field label="Telephone (optionnel)">
          <input type="tel" value={data.phone}
            onChange={e => setData({ ...data, phone: e.target.value })}
            className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="contact-phone" />
        </Field>

        <Field label="Votre demande *">
          <textarea rows={4} value={data.message}
            onChange={e => setData({ ...data, message: e.target.value })}
            placeholder="Decrivez brievement votre besoin (declaration T1, T2, comptabilite, etc.)"
            className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required data-testid="contact-message" />
        </Field>

        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="contact-error">{error}</p>}

        <button type="submit" disabled={sending}
          className="w-full bg-blue-600 text-white rounded-lg py-3 font-medium hover:bg-blue-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
          data-testid="contact-submit">
          {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi...</> : <><Send className="w-4 h-4" /> Envoyer la demande</>}
        </button>

        <p className="text-[11px] text-gray-400 text-center">
          Vos informations sont stockees uniquement pour traiter votre demande.
        </p>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
